import type {ExchangeNodeName} from "#material/exchange/node-name"
import type {PerEngine} from "#material/nodes/base/material/validation/engine"
import type {SettingValueOfType, SocketValueOfType} from "#material/sockets/definition"
import type {MaterialNodeInputs, MaterialNodeSettings} from "#material/sockets/parameters"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import type {Color} from "@cm/graph-v3"

/**
 * Names a setting of this node and the values of it that keep the item on screen, so a mode can hide the
 * sockets it does not use the way Blender does.
 */
export type UiItemCondition<Sockets extends MaterialNodeSockets> = {
    [Name in keyof MaterialNodeSettings<Sockets> & string]:
        | {setting: Name; oneOf: readonly (MaterialNodeSettings<Sockets>[Name] | undefined)[]}
        /** The weight that switches a group of sockets on: they do nothing at all while it is zero. */
        | {setting: Name; whenSet: true}
}[keyof MaterialNodeSettings<Sockets> & string]

/** A socket can depend on more than one thing, and then every one of them has to hold for it to be of any use. */
export type UiItemVisibility<Sockets extends MaterialNodeSockets> = UiItemCondition<Sockets> | readonly UiItemCondition<Sockets>[]

export const conditionsOf = <Sockets extends MaterialNodeSockets>(visibleWhen: UiItemVisibility<Sockets> | undefined): readonly UiItemCondition<Sockets>[] =>
    visibleWhen === undefined ? [] : Array.isArray(visibleWhen) ? visibleWhen : [visibleWhen as UiItemCondition<Sockets>]

export type MaterialUiInfo<Sockets extends MaterialNodeSockets = MaterialNodeSockets> = {
    color: string
    editor: {
        disableable: boolean
        disabled: boolean
        position: {x: number; y: number}
        width?: string
    }
    id: string
    label: string
    name: ExchangeNodeName | null
    items: {
        definition: MaterialSocketUiDefinition<Sockets> | MaterialCustomComponentUiDefinition
        // TODO: use proper value types
        inputValue: unknown
        nodeId: string
        scalarValue: unknown
        uniqueId: string
    }[]
    support: PerEngine<boolean | {full: boolean; remark?: string}>
}

export type MaterialCustomComponentUiDefinition = {
    componentClass: "textureRevision" | "imageTextureSet" | "rgbCurve" | "textureFromSet" | "colorRamp"
    custom: true
    input?: false
    output?: false
    setting?: false
}

export type MaterialInputUiDefinitionOptions<Sockets extends MaterialNodeSockets> = {
    [SocketName in keyof Sockets["inputs"]]: {
        custom?: false
        // can other nodes be connected to the input side of this socket?
        input: true
        defaultValue?: SocketValueOfType<Sockets["inputs"][SocketName]>
        label: string
        name: SocketName
        // can other nodes be connected to the output side of this socket?
        output?: SocketName extends keyof Sockets["outputs"] ? boolean : false
        // can the value be set manually (as opposed to connecting another node)?
        setting?: SocketName extends keyof Sockets["settings"] ? boolean : false
        visibleWhen?: UiItemVisibility<Sockets>
        valueType: Sockets["inputs"][SocketName]
    }
}
export type MaterialSettingUiDefinitionOptions<Sockets extends MaterialNodeSockets> = {
    [SocketName in keyof Sockets["settings"]]: {
        custom?: false
        // can other nodes be connected to the input side of this socket?
        input: SocketName extends keyof Sockets["inputs"] ? boolean : false
        defaultValue?: SettingValueOfType<Sockets["settings"][SocketName]>
        label: string
        name: SocketName
        // can other nodes be connected to the output side of this socket?
        output?: SocketName extends keyof Sockets["outputs"] ? boolean : false
        range?: Sockets["settings"][SocketName] extends "float" ? {min?: number; max?: number} | null : never
        // can the value be set manually (as opposed to connecting another node)?
        setting: true
        visibleWhen?: UiItemVisibility<Sockets>
        valueType: Sockets["settings"][SocketName]
    }
}
export type MaterialSettingExtensionUiDefinitionOptions<Sockets extends MaterialNodeSockets> = {
    [SocketName in keyof Sockets["settingsExtensions"]]: {
        custom?: false
        // can other nodes be connected to the input side of this socket?
        input: SocketName extends keyof Sockets["inputs"] ? boolean : false
        defaultValue?: NonNullable<Sockets["settingsExtensions"][SocketName]>
        label: string
        name: SocketName
        options?: {label: string; value: NonNullable<Sockets["settingsExtensions"][SocketName]>}[]
        // can other nodes be connected to the output side of this socket?
        output?: SocketName extends keyof Sockets["outputs"] ? boolean : false
        range?: Sockets["settingsExtensions"][SocketName] extends number ? {min?: number; max?: number} | null : never
        // can the value be set manually (as opposed to connecting another node)?
        setting: true
        visibleWhen?: UiItemVisibility<Sockets>
        valueType: NonNullable<Sockets["settingsExtensions"][SocketName]> extends number ? "int" : "string"
    }
}
export type MaterialOutputUiDefinitionOptions<Sockets extends MaterialNodeSockets> = {
    [SocketName in keyof Sockets["outputs"]]: {
        custom?: false
        // can other nodes be connected to the input side of this socket?
        input?: SocketName extends keyof Sockets["inputs"] ? boolean : false
        defaultValue?: SettingValueOfType<Sockets["outputs"][SocketName]>
        label: string
        name: SocketName
        options?: Sockets["outputs"][SocketName] extends "string" ? {label: string; value: string}[] : never
        // can other nodes be connected to the output side of this socket?
        output: true
        range?: Sockets["outputs"][SocketName] extends "float" ? {min?: number; max?: number} | null : never
        // can the value be set manually (as opposed to connecting another node)?
        setting?: SocketName extends keyof Sockets["settings"] ? boolean : false
        visibleWhen?: UiItemVisibility<Sockets>
        valueType: Sockets["outputs"][SocketName]
    }
}

export type MaterialInputUiDefinition<Sockets extends MaterialNodeSockets = MaterialNodeSockets> = MaterialInputUiDefinitionOptions<Sockets>[string &
    keyof MaterialInputUiDefinitionOptions<Sockets>]
export type MaterialSettingUiDefinition<Sockets extends MaterialNodeSockets = MaterialNodeSockets> =
    | MaterialSettingUiDefinitionOptions<Sockets>[string & keyof MaterialSettingUiDefinitionOptions<Sockets>]
    | MaterialSettingExtensionUiDefinitionOptions<Sockets>[string & keyof MaterialSettingExtensionUiDefinitionOptions<Sockets>]
export type MaterialOutputUiDefinition<Sockets extends MaterialNodeSockets = MaterialNodeSockets> = MaterialOutputUiDefinitionOptions<Sockets>[string &
    keyof MaterialOutputUiDefinitionOptions<Sockets>]

export type MaterialSocketUiDefinition<Sockets extends MaterialNodeSockets = MaterialNodeSockets> =
    | MaterialInputUiDefinition<Sockets>
    | MaterialSettingUiDefinition<Sockets>
    | MaterialOutputUiDefinition<Sockets>

export type MaterialUiDefinition<Sockets extends MaterialNodeSockets> = {
    color: string
    description?: string
    disableable?: boolean
    items: (MaterialSocketUiDefinition<Sockets> | MaterialCustomComponentUiDefinition)[]
    label: string
    support: {
        cycles:
            | boolean
            | {
                  full: false
                  remark: string
              }
        three:
            | boolean
            | {
                  full: false
                  remark: string
              }
    }
    width?: number
}

export type MaterialUiParameters = {
    disabled?: boolean
    internalColor?: Color
    internalLabel?: string
    position?: {
        x: number
        y: number
    }
}

/** A socket the current mode does not use, unless something is wired into it and the connection needs an anchor. */
export const isUiItemVisible = <Sockets extends MaterialNodeSockets>(
    item: MaterialSocketUiDefinition<Sockets> | MaterialCustomComponentUiDefinition,
    settings: MaterialNodeSettings<Sockets>,
    inputs: MaterialNodeInputs<Sockets>,
): boolean => {
    if (item.custom || item.visibleWhen === undefined) return true
    const holds = (condition: UiItemCondition<Sockets>) => {
        const {setting} = condition
        if ("oneOf" in condition) return condition.oneOf.some((allowed) => allowed === settings[setting])
        return carriesAValue(settings[setting]) || inputs[setting as keyof MaterialNodeInputs<Sockets>] !== undefined
    }
    if (conditionsOf<Sockets>(item.visibleWhen).every(holds)) return true
    return item.input === true && inputs[item.name] !== undefined
}

/** Whether a weight amounts to anything: every socket that switches a group on is zero when it is off. */
const carriesAValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return false
    if (typeof value === "number") return value !== 0
    if (typeof value === "boolean") return value
    // a colour is stored with an alpha of 1 even when it is black, and alpha says nothing about what it carries
    if (typeof value === "object") return Object.entries(value).some(([component, amount]) => component !== "a" && (typeof amount !== "number" || amount !== 0))
    return true
}
